/**
 * One past scheduled-digest delivery.
 *
 * Period dates are the agency's own calendar days, resolved server-side — the stored instants are
 * the local day's boundaries in UTC, so formatting them in the browser would show the end date a
 * day late for any agency west of UTC.
 */
export interface DigestDelivery {
  periodStart: string;
  periodEnd: string;
  status: DigestDeliveryStatus;
  /** A count, never the addresses. */
  recipientCount: number;
  attemptCount: number;
  completedAtUtc: string | null;
  /** Fixed category when it failed; never raw error text. */
  failureCategory: string | null;
}

export type DigestDeliveryStatus = 'Claimed' | 'Sent' | 'Failed' | 'Indeterminate';

/**
 * Plain-language explanation per status.
 *
 * "Indeterminate" is the one that needs saying out loud: it means the send was started and its
 * outcome was never recorded, so nobody can tell whether the digest arrived. It is deliberately
 * never retried automatically — re-sending on a guess is how a chief gets the same QA report twice.
 */
export const DIGEST_STATUS_DESCRIPTION: Record<DigestDeliveryStatus, string> = {
  Sent: 'Handed to the mail provider for delivery.',
  Failed: 'The send did not go out. Retried automatically while under the attempt limit.',
  Claimed: 'In progress.',
  Indeterminate:
    'Started but never confirmed — it is not known whether this digest was delivered. It is not re-sent automatically, to avoid a duplicate.',
};
